import { useState, useCallback, useEffect } from 'react';
import {
  Plus,
  Trash2,
  Loader2,
  Phone,
  Mail,
  MessageSquare,
  Users,
  FileText,
  MoreHorizontal,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { api, ContactInteraction, InteractionType } from '@/lib/api';

const INTERACTION_ICONS: Record<InteractionType, React.ElementType> = {
  note: FileText,
  call: Phone,
  email: Mail,
  meeting: Users,
  message: MessageSquare,
  other: MoreHorizontal,
};

const INTERACTION_LABELS: Record<InteractionType, string> = {
  note: 'Note',
  call: 'Call',
  email: 'Email',
  meeting: 'Meeting',
  message: 'Message',
  other: 'Other',
};

const INTERACTION_COLORS: Record<InteractionType, string> = {
  note: 'bg-blue-100 text-blue-700',
  call: 'bg-green-100 text-green-700',
  email: 'bg-purple-100 text-purple-700',
  meeting: 'bg-orange-100 text-orange-700',
  message: 'bg-cyan-100 text-cyan-700',
  other: 'bg-gray-100 text-gray-700',
};

export function InteractionsTimeline({ contactId }: { contactId: string }) {
  const [interactions, setInteractions] = useState<ContactInteraction[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // New interaction form
  const [newType, setNewType] = useState<InteractionType>('note');
  const [newSummary, setNewSummary] = useState('');
  const [newDetails, setNewDetails] = useState('');
  const [showNewForm, setShowNewForm] = useState(false);

  const fetchInteractions = useCallback(async () => {
    try {
      const result = await api.listInteractions(contactId);
      setInteractions(result.interactions);
      setTotal(result.total);
    } catch {
      // Non-critical on load
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => {
    fetchInteractions();
  }, [fetchInteractions]);

  const handleCreate = useCallback(async () => {
    if (!newSummary.trim()) return;
    setCreating(true);
    try {
      const interaction = await api.createInteraction(contactId, {
        interactionType: newType,
        summary: newSummary.trim(),
        details: newDetails.trim() || null,
      });
      setInteractions((prev) => [interaction, ...prev]);
      setTotal((prev) => prev + 1);
      setNewSummary('');
      setNewDetails('');
      setShowNewForm(false);
    } catch (err) {
      toast({
        title: 'Failed to add interaction',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setCreating(false);
    }
  }, [contactId, newType, newSummary, newDetails]);

  const handleDelete = useCallback(async (id: string) => {
    setDeletingId(id);
    try {
      await api.deleteInteraction(id);
      setInteractions((prev) => prev.filter((i) => i.id !== id));
      setTotal((prev) => prev - 1);
    } catch (err) {
      toast({
        title: 'Failed to delete interaction',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setDeletingId(null);
    }
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-3">
        <Label className="text-base font-semibold">
          Interactions{' '}
          {total > 0 && <span className="text-muted-foreground font-normal">({total})</span>}
        </Label>
        {!showNewForm && (
          <Button type="button" size="sm" variant="outline" onClick={() => setShowNewForm(true)}>
            <Plus className="h-3.5 w-3.5 mr-1" />
            Add
          </Button>
        )}
      </div>

      {showNewForm && (
        <div className="rounded-md border p-3 mb-3 space-y-2">
          <div className="flex gap-2">
            <Select value={newType} onValueChange={(v) => setNewType(v as InteractionType)}>
              <SelectTrigger className="w-32 h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(INTERACTION_LABELS) as InteractionType[]).map((type) => (
                  <SelectItem key={type} value={type}>
                    {INTERACTION_LABELS[type]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              value={newSummary}
              onChange={(e) => setNewSummary(e.target.value)}
              placeholder="What happened?"
              className="flex-1 h-8 text-sm"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleCreate();
                }
              }}
            />
          </div>
          <Textarea
            value={newDetails}
            onChange={(e) => setNewDetails(e.target.value)}
            placeholder="Details (optional)"
            rows={2}
            className="text-sm"
          />
          <div className="flex gap-2 justify-end">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                setShowNewForm(false);
                setNewSummary('');
                setNewDetails('');
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={creating || !newSummary.trim()}
              onClick={handleCreate}
            >
              {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save'}
            </Button>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto space-y-2 max-h-[50vh]">
        {interactions.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">
            No interactions recorded yet. Click "Add" to log your first one.
          </p>
        ) : (
          interactions.map((interaction) => {
            const Icon = INTERACTION_ICONS[interaction.interactionType] || FileText;
            return (
              <div
                key={interaction.id}
                className="group/entry flex items-start gap-2.5 rounded-md border p-2.5"
              >
                <div
                  className={`mt-0.5 rounded-full p-1.5 flex-shrink-0 ${
                    INTERACTION_COLORS[interaction.interactionType] || INTERACTION_COLORS.other
                  }`}
                >
                  <Icon className="h-3 w-3" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-[10px] px-1 py-0">
                      {INTERACTION_LABELS[interaction.interactionType]}
                    </Badge>
                    <p className="text-xs text-muted-foreground">
                      {new Date(interaction.occurredAt).toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                  <p className="text-sm mt-1">{interaction.summary}</p>
                  {interaction.details && (
                    <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">
                      {interaction.details}
                    </p>
                  )}
                </div>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6 flex-shrink-0 opacity-0 group-hover/entry:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                  onClick={() => handleDelete(interaction.id)}
                  disabled={deletingId === interaction.id}
                  title="Delete interaction"
                >
                  {deletingId === interaction.id ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Trash2 className="h-3 w-3" />
                  )}
                </Button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
